import React from 'react'
import PropTypes from 'prop-types'
import { Card, Stack, Typography } from '@mui/material'
import { alpha, keyframes, useTheme } from '@mui/material/styles'
import { CheckCircleOutline, ReportOutlined } from '@mui/icons-material'
import { useSmartTranslation } from '../../../../../generic/hooks/useSmartTranslation'
import WorkerRequiredButton from '../../../common/WorkerRequiredButton'

/**
 * Animated shadow pulse used to highlight an active incidence state.
 */
const pulseAnimation = keyframes`
  0% { box-shadow: 0 0 0 0 rgba(211, 47, 47, 0.4); }
  70% { box-shadow: 0 0 0 10px rgba(211, 47, 47, 0); }
  100% { box-shadow: 0 0 0 0 rgba(211, 47, 47, 0); }
`

/**
 * Shared button styles for primary actions in the widget (create/close).
 */
const getPrimaryActionButtonSx = (compact) => ({
  py: compact ? 1.6 : 2,
  borderRadius: 3,
  boxShadow: 3,
  fontWeight: 'bold',
  fontSize: compact ? '0.95rem' : '1rem',
  textTransform: 'none'
})

/**
 * Status card for device incidences: no active incidence or active incidence.
 *
 * Props:
 * - If no active incidence: shows OK status and a button to create an incidence.
 * - If active incidence: allows opening details by clicking on the card and closing via button.
 *
 * @param {object} props
 * @param {boolean} props.hasIncidence Indicates if there is an active incidence.
 * @param {string} [props.incidenceName] Name of the active incidence.
 * @param {string} [props.startTime] Start time of the active incidence.
 * @param {Function} props.onCreateClick Callback to start incidence creation.
 * @param {Function} props.onDetailClick Callback to open incidence details.
 * @param {Function} props.onCloseClick Callback to close the active incidence.
 * @param {boolean} [props.closing=false] Blocks the close button while saving.
 * @returns {JSX.Element}
 */
const IncidenceStatusCard = ({
  hasIncidence,
  incidenceName,
  startTime,
  onCreateClick,
  onDetailClick,
  onCloseClick,
  closing = false,
  compact = false
}) => {
  const { t_ } = useSmartTranslation()
  const theme = useTheme()
  const iconSize = compact ? '4rem' : '5rem'
  const cardPadding = compact ? 2 : 3
  const primaryActionButtonSx = getPrimaryActionButtonSx(compact)

  if (!hasIncidence) {
    return (
      <Stack spacing={2} sx={{ height: '100%' }}>
        <Card
          elevation={3}
          sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            px: cardPadding,
            bgcolor: alpha(theme.palette.success.main, 0.78),
            color: theme.palette.success.contrastText,
            borderRadius: 3,
            border: `1px solid ${alpha(theme.palette.success.main, 0.35)}`,
            width: '100%'
          }}
        >
          <CheckCircleOutline sx={{ fontSize: iconSize, mb: 1 }} />
          <Typography variant='subtitle1' sx={{ opacity: 0.92, textAlign: 'center' }}>
            {t_('No active incidences')}
          </Typography>
        </Card>

        <WorkerRequiredButton
          variant='contained'
          color='error'
          fullWidth
          onClick={onCreateClick}
          sx={primaryActionButtonSx}
          startIcon={<ReportOutlined />}
        >
          {t_('CREATE INCIDENCE')}
        </WorkerRequiredButton>
      </Stack>
    )
  }

  return (
    <Stack spacing={2} sx={{ height: '100%' }}>
      <Card
        elevation={3}
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          px: cardPadding,
          bgcolor: alpha(theme.palette.error.main, 0.78),
          color: theme.palette.error.contrastText,
          borderRadius: 3,
          border: `1px solid ${alpha(theme.palette.error.main, 0.38)}`,
          animation: `${pulseAnimation} 2s infinite`,
          cursor: 'pointer'
        }}
        onClick={onDetailClick}
        role='button'
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') onDetailClick?.()
        }}
        aria-label={t_('VIEW DETAILS')}
      >
        <ReportOutlined sx={{ fontSize: iconSize, mb: 1 }} />
        <Typography variant={compact ? 'h6' : 'h5'} align='center' fontWeight='bold' sx={{ px: 1 }}>
          {incidenceName || t_('INCIDENCE ACTIVE')}
        </Typography>
        <Typography variant='subtitle1' sx={{ mt: 1, opacity: 0.9 }}>
          {startTime}
        </Typography>
      </Card>

      <WorkerRequiredButton
        variant='contained'
        color='success'
        fullWidth
        onClick={onCloseClick}
        disabled={closing}
        sx={primaryActionButtonSx}
        startIcon={<CheckCircleOutline />}
      >
        {closing ? t_('CLOSING...') : t_('CLOSE INCIDENCE')}
      </WorkerRequiredButton>
    </Stack>
  )
}

IncidenceStatusCard.propTypes = {
  hasIncidence: PropTypes.bool,
  incidenceName: PropTypes.string,
  startTime: PropTypes.string,
  onCreateClick: PropTypes.func.isRequired,
  onDetailClick: PropTypes.func.isRequired,
  onCloseClick: PropTypes.func.isRequired,
  closing: PropTypes.bool,
  compact: PropTypes.bool
}

export default IncidenceStatusCard
